import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTintedBackground } from '../../hooks/useTintedBackground';
import { NewConversationButton } from '../common/NewConversationButton';
import {
  FiGrid,
  FiList,
  FiSearch,
  FiX,
  FiArrowUp,
  FiArrowDown,
  FiSliders,
} from 'react-icons/fi';

type ViewMode = 'grid' | 'table';
type SortKey = 'lastActivity' | 'messageCount' | 'created' | 'personaName';

interface ChatSessionsCommandBarProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  sortKey: SortKey;
  onSortKeyChange: (key: SortKey) => void;
  sortDir: 'asc' | 'desc';
  onToggleSortDir: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  resultCount: number;
  totalCount: number;
}

const sortOptions: { key: SortKey; label: string; defaultDir: 'asc' | 'desc' }[] = [
  { key: 'lastActivity', label: 'Last Activity', defaultDir: 'desc' },
  { key: 'messageCount', label: 'Message Count', defaultDir: 'desc' },
  { key: 'created', label: 'Created Date', defaultDir: 'desc' },
  { key: 'personaName', label: 'Persona Name', defaultDir: 'asc' },
];

export const ChatSessionsCommandBar: React.FC<ChatSessionsCommandBarProps> = ({
  viewMode,
  onViewModeChange,
  sortKey,
  onSortKeyChange,
  sortDir,
  onToggleSortDir,
  searchQuery,
  onSearchChange,
  resultCount,
  totalCount,
}) => {
  const { darkMode } = useTheme();
  
  const tintedBg = useTintedBackground({
    tintColor: darkMode ? '#3b82f6' : '#6366f1',
    enabled: true
  });
  
  const currentSortOption = sortOptions.find(opt => opt.key === sortKey);
  
  return (
    <div
      ref={tintedBg.ref}
      className={`
        relative rounded-lg shadow-lg border-l-4 mb-4 overflow-hidden
        ${darkMode
          ? 'border-emerald-600 bg-gray-800'
          : 'border-emerald-500 bg-white shadow-sm'
        }
      `}
      style={tintedBg.style}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={tintedBg.overlayStyle}
      />
      
      <div className="relative z-10 px-4 py-3 flex flex-col md:flex-row md:items-center gap-3">
        {/* Title */}
        <div className="shrink-0">
          <h1 className={`text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r ${
            darkMode ? 'from-emerald-400 to-teal-400' : 'from-emerald-600 to-teal-600'
          }`}>Conversations</h1>
          <p className={`text-[10px] italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Your conversational nerve center</p>
        </div>
        {/* Search */}
        <div className="flex-1 min-w-0">
          <div className={`
            relative flex items-center
            ${darkMode ? 'text-gray-300' : 'text-gray-600'}
          `}>
            <FiSearch className="absolute left-3 w-4 h-4 opacity-50" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search sessions by persona or message..."
              className={`
                w-full pl-10 pr-10 py-2 rounded-lg text-sm transition-all
                border focus:outline-none focus:ring-2
                ${darkMode 
                  ? 'bg-gray-800/80 border-gray-600 text-gray-100 placeholder-gray-500 focus:ring-blue-500/50 focus:border-blue-500' 
                  : 'bg-white/80 border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500/30 focus:border-blue-500'
                }
              `}
            />
            {searchQuery && (
              <button
                onClick={() => onSearchChange('')}
                className={`
                  absolute right-3 p-0.5 rounded-full transition-colors
                  ${darkMode 
                    ? 'hover:bg-gray-700 text-gray-400' 
                    : 'hover:bg-gray-200 text-gray-500'
                  }
                `}
              >
                <FiX className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className={`
            flex items-center p-1 rounded-lg border
            ${darkMode 
              ? 'bg-gray-800/50 border-gray-700' 
              : 'bg-white/50 border-gray-200'
            }
          `}>
            <button
              onClick={() => onViewModeChange('grid')}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all
                ${viewMode === 'grid'
                  ? darkMode
                    ? 'bg-blue-600 text-white shadow-inner'
                    : 'bg-blue-500 text-white shadow-inner'
                  : darkMode
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }
              `}
              title="Grid view"
            >
              <FiGrid className="w-4 h-4" />
              <span className="hidden sm:inline">Grid</span>
            </button>
            <button
              onClick={() => onViewModeChange('table')}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all
                ${viewMode === 'table'
                  ? darkMode
                    ? 'bg-blue-600 text-white shadow-inner'
                    : 'bg-blue-500 text-white shadow-inner'
                  : darkMode
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }
              `}
              title="Table view"
            >
              <FiList className="w-4 h-4" />
              <span className="hidden sm:inline">Table</span>
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <div className={`
              relative flex items-center
              ${darkMode ? 'text-gray-300' : 'text-gray-600'}
            `}>
              <FiSliders className="absolute left-2 w-3.5 h-3.5 opacity-50" />
              <select
                value={sortKey}
                onChange={(e) => onSortKeyChange(e.target.value as SortKey)}
                className={`
                  pl-7 pr-8 py-1.5 rounded-lg text-sm appearance-none cursor-pointer
                  border focus:outline-none focus:ring-2
                  ${darkMode 
                    ? 'bg-gray-800/80 border-gray-600 text-gray-100 focus:ring-blue-500/50 focus:border-blue-500' 
                    : 'bg-white/80 border-gray-300 text-gray-900 focus:ring-blue-500/30 focus:border-blue-500'
                  }
                `}
              >
                {sortOptions.map(option => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <button
              onClick={onToggleSortDir}
              className={`
                p-1.5 rounded-lg border transition-all
                ${darkMode 
                  ? 'bg-gray-800/80 border-gray-600 text-gray-300 hover:bg-gray-700' 
                  : 'bg-white/80 border-gray-300 text-gray-600 hover:bg-gray-100'
                }
              `}
              title={`Sort ${sortDir === 'asc' ? 'ascending' : 'descending'}`}
            >
              {sortDir === 'asc' ? (
                <FiArrowUp className="w-4 h-4" />
              ) : (
                <FiArrowDown className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <span className={`
            text-sm whitespace-nowrap
            ${darkMode ? 'text-gray-400' : 'text-gray-500'}
          `}>
            {resultCount} of {totalCount}
          </span>
          
          <NewConversationButton variant="primary" />
        </div>
      </div>
    </div>
  );
};
